import { Grid, FormControl, Autocomplete, TextField } from '@mui/material';
import { FC, SetStateAction, Dispatch, useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  FiltersLayout,
  FiltersButtons,
  SearchField,
  VendorDropdown,
  DateRangePicker,
  NewsCategoryDropdown
} from '../../../../components';
import { IDateRange, IResponse, IDomainUser } from '../../../../models';
import { getDomainUsers } from '../../../../fetch';

interface INewsFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  setSelectedTerritoryId?: (val: string | number) => void;
  selectedTerritoryId?: string | number;
  setHasAppliedFilters: Dispatch<SetStateAction<boolean>>;
  hasAppliedFilters: boolean;
  setSearchSubjectValue?: (val: string) => void;
  setSearchMessageValue?: (val: string) => void;
  searchMessageValue?: string;
  searchSubjectValue?: string;
  handleSubjectSearch?: (val: string) => void;
  handleMessageSearch?: (val: string) => void;
  selectedDisplayDateRange?: IDateRange | null;
  setSelectedDisplayDateRange?: Dispatch<SetStateAction<IDateRange>>;
  selectedTakedownDateRange?: IDateRange | null;
  setSelectedTakedownDateRange?: Dispatch<SetStateAction<IDateRange>>;
  selectedCreatedDateRange?: IDateRange | null;
  setSelectedCreatedDateRange?: Dispatch<SetStateAction<IDateRange>>;
  setSelectedVendorId?: (val: string | number) => void;
  selectedVendorId?: string | number;
  setSelectedCategory?: (val: string) => void;
  selectedCategory?: string;
  selectedUser?: IDomainUser | null;
  setSelectedUser?: (val: IDomainUser | null) => void;
}

export const NewsFilters: FC<INewsFilters> = ({
  isLoading,
  applyFilters,
  setHasAppliedFilters,
  hasAppliedFilters,
  handleSubjectSearch,
  handleMessageSearch,
  searchSubjectValue,
  searchMessageValue,
  setSearchSubjectValue,
  setSearchMessageValue,
  setSelectedVendorId,
  selectedVendorId,
  setSelectedCategory,
  selectedCategory,
  selectedDisplayDateRange,
  setSelectedDisplayDateRange,
  selectedTakedownDateRange,
  setSelectedTakedownDateRange,
  selectedCreatedDateRange,
  setSelectedCreatedDateRange,
  setSelectedUser,
  selectedUser
}) => {
  const [assignToInput, setAssignToInput] = useState('');
  const { isLoading: isLoadingInternalUsers, data: internalUsersData } = useQuery<
    IResponse<IDomainUser[]>,
    Error
  >(['domain-users'], () =>
    getDomainUsers({
      sortBy: 'firstName'
    })
  );
  const internalUsers: IDomainUser[] = useMemo(
    () => internalUsersData?.records ?? [],
    [internalUsersData]
  );
  return (
    <FiltersLayout withExpand>
      {handleSubjectSearch && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <SearchField
            isDisabled={isLoading}
            placeholder="Search by subject..."
            handleSearch={handleSubjectSearch}
            searchValue={searchSubjectValue}
            setSearchValue={setSearchSubjectValue}
          />
        </Grid>
      )}
      {handleMessageSearch && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <SearchField
            isDisabled={isLoading}
            placeholder="Search by message..."
            handleSearch={handleMessageSearch}
            searchValue={searchMessageValue}
            setSearchValue={setSearchMessageValue}
          />
        </Grid>
      )}
      {setSelectedVendorId && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <VendorDropdown
            isDisabled={isLoading}
            value={selectedVendorId}
            handleChange={value => {
              setSelectedVendorId(value);
            }}
          />
        </Grid>
      )}
      {setSelectedUser && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <Autocomplete
              id="assign-to"
              value={selectedUser}
              onChange={(event, newValue: IDomainUser) => {
                setSelectedUser(newValue);
              }}
              inputValue={assignToInput}
              onInputChange={(event, newInputValue) => {
                setAssignToInput(newInputValue);
              }}
              options={internalUsers}
              getOptionLabel={(option: any) => option?.displayName ?? option}
              filterSelectedOptions
              renderOption={(props, option: IDomainUser) => {
                return (
                  <li {...props} key={`${option.accountName}`}>
                    {option.displayName}
                  </li>
                );
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  disabled={isLoadingInternalUsers}
                  label={isLoadingInternalUsers ? 'Loading...' : 'Employee'}
                  size="small"
                />
              )}
            />
          </FormControl>
        </Grid>
      )}
      {setSelectedCategory && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <NewsCategoryDropdown
            isDisabled={isLoading}
            value={selectedCategory}
            handleChange={value => {
              setSelectedCategory(value);
            }}
          />
        </Grid>
      )}
      {setSelectedDisplayDateRange && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <DateRangePicker
              id="display-date-range"
              label="Display Date Range"
              value={selectedDisplayDateRange}
              isDisabled={isLoading}
              onChange={value => {
                setSelectedDisplayDateRange(value);
              }}
              fullWidth
              inputSize="small"
            />
          </FormControl>
        </Grid>
      )}
      {setSelectedTakedownDateRange && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <DateRangePicker
              id="takedown-date-range"
              label="Takedown Date Range"
              value={selectedTakedownDateRange}
              isDisabled={isLoading}
              onChange={value => {
                setSelectedTakedownDateRange(value);
              }}
              fullWidth
              inputSize="small"
            />
          </FormControl>
        </Grid>
      )}
      {setSelectedCreatedDateRange && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <DateRangePicker
              id="created-date-range"
              label="Created Date Range"
              value={selectedCreatedDateRange}
              isDisabled={isLoading}
              onChange={value => {
                setSelectedCreatedDateRange(value);
              }}
              fullWidth
              inputSize="small"
            />
          </FormControl>
        </Grid>
      )}
      <Grid item xs={12} md={5} xl={3}>
        <FiltersButtons
          hasAppliedFilters={hasAppliedFilters}
          isDisabled={isLoading}
          handleApplyFilters={() => {
            setHasAppliedFilters(true);
            applyFilters();
          }}
          handleResetFilters={() => {
            setHasAppliedFilters(false);
            setSelectedVendorId && setSelectedVendorId('');
            setSelectedCreatedDateRange &&
              setSelectedCreatedDateRange({
                startDate: null,
                endDate: null,
                key: 'selection',
                inputValue: ''
              });
            setSelectedTakedownDateRange &&
              setSelectedTakedownDateRange({
                startDate: null,
                endDate: null,
                key: 'selection',
                inputValue: ''
              });
            setSelectedDisplayDateRange &&
              setSelectedDisplayDateRange({
                startDate: null,
                endDate: null,
                key: 'selection',
                inputValue: ''
              });
            setSelectedUser && setSelectedUser(null);
            setSelectedCategory && setSelectedCategory('');
            setSearchSubjectValue && setSearchSubjectValue('');
            setSearchMessageValue && setSearchMessageValue('');
            applyFilters(true);
          }}
        />
      </Grid>
    </FiltersLayout>
  );
};
